import {
  applyPinchPreviewTransform,
  clearPinchPreviewTransform,
  pinchPreviewContentOrigin,
} from 'features/knowledgeDocumentViewer/components/viewer/continuous/applyPinchPreviewTransform'
import type { ContinuousZoomAnchor } from 'features/knowledgeDocumentViewer/components/viewer/continuous/resolveContinuousZoomScroll'
import {
  resolvePinchZoom,
  touchPairDistance,
} from 'features/knowledgeDocumentViewer/components/viewer/continuous/resolvePinchZoom'
import { type RefObject, useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'

export function useContinuousPinchZoom(
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  snapshotZoomAnchor: (focal?: {
    x: number
    y: number
  }) => ContinuousZoomAnchor | null,
  applyAnchoredZoom: (level: number, anchor: ContinuousZoomAnchor) => void,
  minZoom: number,
  maxZoom: number
) {
  const snapshotRef = useRef(snapshotZoomAnchor)
  snapshotRef.current = snapshotZoomAnchor
  const applyRef = useRef(applyAnchoredZoom)
  applyRef.current = applyAnchoredZoom
  const contentRefStable = useRef(contentRef)
  contentRefStable.current = contentRef
  const minZoomRef = useRef(minZoom)
  minZoomRef.current = minZoom
  const maxZoomRef = useRef(maxZoom)
  maxZoomRef.current = maxZoom

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    let session: {
      anchor: ContinuousZoomAnchor
      startZoom: number
      startDistance: number
      lastZoom: number
      originX: number
      originY: number
    } | null = null

    const focalFromTouches = (touches: TouchList) => {
      const rect = container.getBoundingClientRect()
      const a = touches.item(0)
      const b = touches.item(1)
      if (!a || !b) return { x: rect.width / 2, y: rect.height / 2 }
      return {
        x: (a.clientX + b.clientX) / 2 - rect.left,
        y: (a.clientY + b.clientY) / 2 - rect.top,
      }
    }

    const beginPinch = (touches: TouchList) => {
      const a = touches.item(0)
      const b = touches.item(1)
      if (!a || !b) return
      const focal = focalFromTouches(touches)
      const anchor = snapshotRef.current(focal)
      if (!anchor) return
      const origin = pinchPreviewContentOrigin(
        container.scrollLeft,
        container.scrollTop,
        anchor.focalX,
        anchor.focalY
      )
      session = {
        anchor,
        startZoom: anchor.oldZoom,
        startDistance: touchPairDistance(a, b),
        lastZoom: anchor.oldZoom,
        originX: origin.x,
        originY: origin.y,
      }
    }

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        event.preventDefault()
        beginPinch(event.touches)
      }
    }

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length < 2) return
      event.preventDefault()
      const a = event.touches.item(0)
      const b = event.touches.item(1)
      if (!a || !b) return
      if (!session) beginPinch(event.touches)
      if (!session) return
      const nextZoom = resolvePinchZoom({
        startZoom: session.startZoom,
        startDistance: session.startDistance,
        currentDistance: touchPairDistance(a, b),
        minZoom: minZoomRef.current,
        maxZoom: maxZoomRef.current,
      })
      session.lastZoom = nextZoom
      const content = contentRefStable.current.current
      if (!content || session.startZoom === 0) return
      applyPinchPreviewTransform(
        content,
        session.originX,
        session.originY,
        nextZoom / session.startZoom
      )
    }

    const endPinch = (event: TouchEvent) => {
      if (event.touches.length >= 2) return
      if (!session) return
      const ending = session
      session = null
      const content = contentRefStable.current.current

      // Commit React layout zoom synchronously before clearing the preview transform
      // so there is no visual flicker between the CSS scale dropping and layout settling.
      if (Math.abs(ending.lastZoom - ending.anchor.oldZoom) > 0.0001) {
        flushSync(() => {
          applyRef.current(ending.lastZoom, ending.anchor)
        })
      }
      if (content) clearPinchPreviewTransform(content)
    }

    const preventSafariPageZoom = (event: Event) => {
      event.preventDefault()
    }

    container.addEventListener('touchstart', onTouchStart, { passive: false })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', endPinch)
    container.addEventListener('touchcancel', endPinch)
    container.addEventListener('gesturestart', preventSafariPageZoom, {
      passive: false,
    })
    container.addEventListener('gesturechange', preventSafariPageZoom, {
      passive: false,
    })
    container.addEventListener('gestureend', preventSafariPageZoom, {
      passive: false,
    })

    return () => {
      const content = contentRefStable.current.current
      if (content) clearPinchPreviewTransform(content)
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', endPinch)
      container.removeEventListener('touchcancel', endPinch)
      container.removeEventListener('gesturestart', preventSafariPageZoom)
      container.removeEventListener('gesturechange', preventSafariPageZoom)
      container.removeEventListener('gestureend', preventSafariPageZoom)
    }
  }, [scrollContainerRef])
}
